import { useNotification } from "@expert/common-ui";
import { trackFullStoryEvent } from "@expert/monitoring";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";

import { ContactEndedError } from "@/amazon-connect/chat/amazonConnectChatSdk";
import { getRootDispatcher } from "@/analytics";
import { useLogger } from "@/sdk/logging/LoggerProvider";

import type { ChatTask } from "./chatTask";

import { useAgentSdk } from "../hooks";
import { CHAT_RUNTIME_ACTIONS, emitChatRuntimeError } from "./emitChatRuntimeError";

/** Generates consistent mutation key for send message operations */
export const getsendChatMessageMutationKey = (chatId: string) => ["sendChatMessage", chatId] as const;

/**
 * UI component that initiated the message send - used for analytics filtering.
 * Extend this type when adding new send sources (e.g., AI autocomplete, AI correction).
 */
export type MessageSendSourceComponent = "message-input";

export interface UseSendChatMessageOptions {
    chatTask: ChatTask | undefined;
    chatId: string | undefined;
    /** UI component that initiated the send - used for analytics and logs */
    sourceComponent: MessageSendSourceComponent;
    /** Additional metadata to include in analytics events (e.g., spellcheck status) */
    extraMetadata?: Record<string, unknown>;
    /** Called immediately before the message is sent. Use for clearing input, scrolling, etc. */
    onBeforeSend?: (message: string) => void;
    /** Called after a successful send */
    onSuccess?: (message: string) => void;
    /** Called when all retries have failed */
    onError?: (error: Error, message: string) => void;
}

export interface UseSendChatMessageResult {
    /** Send a message to the customer. Returns a promise that resolves when sent. */
    sendChatMessage: (message: string) => Promise<void>;
    /** Whether a message is currently being sent */
    isPending: boolean;
    /** Number of failed attempts (resets on success) */
    failureCount: number;
}

/**
 * Shared hook for sending chat messages to customers.
 *
 * Uses a consistent mutation key per chatId, allowing multiple components
 * to observe the sending state via `useIsSendingChatMessage`.
 *
 * @example
 * // In MessageInput - clear input on send
 * const { sendChatMessage } = useSendChatMessage({
 *   chatTask,
 *   chatId,
 *   sourceComponent: "message-input",
 *   onBeforeSend: () => {
 *     setLocalInput("");
 *     scrollToBottom(viewport);
 *   },
 * });
 *
 */
export function useSendChatMessage({
    chatTask,
    chatId,
    sourceComponent,
    extraMetadata,
    onBeforeSend,
    onSuccess,
    onError,
}: UseSendChatMessageOptions): UseSendChatMessageResult {
    const logger = useLogger({ module: "useSendChatMessage", supportTeam: "messaging" });
    const agentSdk = useAgentSdk();
    const notification = useNotification();

    const chatAnalyticDispatcher = useMemo(
        () =>
            getRootDispatcher()
                .withIdentities(chatTask ? { ChatId: chatTask.chatId } : {})
                .withExtra(chatTask ? { sdkProvider: agentSdk.getSdkTypeForTask(chatTask) } : {}),
        [chatTask, agentSdk],
    );

    const submitMessage = async (message: string) => {
        if (!message.trim()) {
            logger.warn({ chatId }, "Attempted to send empty message - blocked");
            return;
        }

        if (!chatTask) {
            logger.warn({ chatId }, "Chat task is required to send message");
            return;
        }
        await agentSdk.sendMessage(chatTask, message);
    };

    const { isPending, submittedAt, failureCount, mutateAsync } = useMutation({
        mutationKey: getsendChatMessageMutationKey(chatId ?? ""),
        retry: (failureCount, error) => (error instanceof ContactEndedError ? false : failureCount < 2),
        mutationFn: submitMessage,
        onMutate: (message: string) => {
            logger.trace({ chatId, messageLength: message.length }, "Sending message");
            onBeforeSend?.(message);
            return { originalMessage: message };
        },
        retryDelay(currentFailureCount, error) {
            logger.warn(
                { chatId, error, failureCount: currentFailureCount },
                `Retry sending message attempt: ${currentFailureCount}`,
            );
            return 1000 * 2 ** currentFailureCount;
        },
        onSuccess: (_data, message) => {
            const analyticsPayload = {
                messageLength: message.length,
                durationMs: submittedAt > 0 ? Date.now() - submittedAt : 0,
                senderType: "expert" as const,
                sourceComponent,
                partner: chatTask?.partner,
                topic: chatTask?.topic,
                language: chatTask?.language,
                sourceApp: chatTask?.sourceApp,
                ...extraMetadata,
            };
            void chatAnalyticDispatcher.dispatchBusinessEvent("ChatMessageSucceeded", analyticsPayload);
            trackFullStoryEvent("ChatMessageSucceeded", analyticsPayload);
            onSuccess?.(message);
        },
        onError: (error, _variables, context) => {
            const errorMessage =
                error instanceof ContactEndedError
                    ? "Message failed to send: chat has ended."
                    : "Message failed to send. Please try again.";
            notification.error(errorMessage);
            const analyticsPayload = {
                errorMessage: error instanceof Error ? error.message : "Unknown error",
                messageLength: context?.originalMessage?.length ?? 0,
                durationMs: submittedAt > 0 ? Date.now() - submittedAt : 0,
                failedAttempts: failureCount,
                senderType: "expert" as const,
                sourceComponent,
                partner: chatTask?.partner,
                topic: chatTask?.topic,
                language: chatTask?.language,
                sourceApp: chatTask?.sourceApp,
                ...extraMetadata,
            };
            void chatAnalyticDispatcher.dispatchBusinessEvent("ChatMessageFailed", analyticsPayload);
            trackFullStoryEvent("ChatMessageFailed", analyticsPayload);
            logger.error({ chatId, error }, "Error sending message");
            if (!(error instanceof ContactEndedError)) {
                emitChatRuntimeError(CHAT_RUNTIME_ACTIONS.messageSendFailed, chatTask?.partner);
            }
            onError?.(error instanceof Error ? error : new Error(String(error)), context?.originalMessage ?? "");
        },
    });

    const sendChatMessage = async (message: string): Promise<void> => {
        await mutateAsync(message);
    };

    return {
        sendChatMessage,
        isPending,
        failureCount,
    };
}
